/**
 * Frontend contract of the backend AccessControl subsystem
 * (symfony_app_code_base/src/AccessControl/**, documented in its CLAUDE.md).
 *
 * Responses may carry restriction metadata in two places:
 *  - root-level `@accessRules` — rules for the entity's real properties;
 *  - `virtual['@accessRules']` — rules for computed/virtual fields (plain names,
 *    the `virtual.` dot-prefix exists only in the backend rules table).
 *
 * Nested resources (e.g. deals inside a placement) are filtered separately and
 * carry their own blocks.
 */

/** Sentinel blocker meaning "every write action is forbidden". Always stands alone. */
export const ALL_BLOCKED = 'all_blocked';

/**
 * Canonical write actions (backend WriteBlockedAction::ALL, emitted in this order).
 * Scalars: null_to_value / value_to_null / value_to_value; collections: append / remove.
 */
export const WRITE_BLOCKED_ACTIONS = [
  ALL_BLOCKED,
  'null_to_value_blocked',
  'value_to_null_blocked',
  'value_to_value_blocked',
  'append_blocked',
  'remove_blocked',
] as const;

export type TWriteBlockedAction = typeof WRITE_BLOCKED_ACTIONS[number];

export const isKnownWriteAction = (v: unknown): v is TWriteBlockedAction => {
  return typeof v === 'string' && (WRITE_BLOCKED_ACTIONS as readonly string[]).includes(v);
};

/** Backend UiState values. */
export type TUiState = 'disabled' | 'hidden';

/**
 * Write-reason registry (backend: block-action-reasons contract, canonical
 * system order first, then seeder-owned static locks).
 */
export type TWriteBlockReasonType =
  | 'access_restricted' // fail-closed: access cannot be safely determined
  | 'user_isnt_responsible' // actor is not the deal's responsible manager
  | 'unlink_disabled_by_policy' // special-developer policy forbids unlink/relink
  | 'status_rejects_links' // placement status rejects new deal links
  | 'opportunity_placement_limit_reached' // NetHunt opportunity already has a placement
  | 'relink_blocked' // adding would silently steal a deal from another placement
  | 'denied' // generic fallback: blocked, but no concrete cause available
  | 'deal_name_readonly' // static special-developer lock (bitrixExternalName)
  | 'client_contacts_readonly'; // static special-developer lock (client name/phones)

/** Read-reason types (readBlockReasons entries) — must not be mistaken for write reasons. */
export type TReadBlockReasonType =
  | 'user_isnt_responsible'
  | 'deal_name_hidden'
  | 'client_contacts_hidden'
  | 'access_restricted'
  | 'denied';

export type TBlockReasonType = TWriteBlockReasonType | TReadBlockReasonType;

export interface IBlockActionReason {
  type: TBlockReasonType;
  message: string | null;
}

export interface IWriteBlocker {
  /** Forbidden write action name, or ALL_BLOCKED for a full lock. */
  blockActionName: TWriteBlockedAction;
  /** One blocker can carry several reasons (responsibility + policy, …). */
  blockActionReasons: IBlockActionReason[];
}

/**
 * A rule ENTRY is present only when the field is restricted (read=false OR
 * writeBlockers non-empty). Fully-allowed fields get no entry; a payload with
 * nothing restricted has no `@accessRules` key at all.
 *
 * When read=false the field's VALUE IS REMOVED from the payload entirely —
 * the entry is how the client tells "restricted" apart from "no data".
 */
export interface IPropertyRule {
  /** Plain property name (no dot-prefixes). */
  name: string;
  read: boolean;
  /**
   * Blacklist of forbidden write actions.
   * `[]` — any write allowed; full lock — single ALL_BLOCKED entry.
   * Treat as UI hints: real enforcement is the backend 403
   * `{title: "Field write access denied", violations: [{field, detail}]}`.
   */
  writeBlockers: IWriteBlocker[];
  readBlockReasons: IBlockActionReason[];
  /** Backend UI hint; passes through even when read=true. */
  ui: TUiState | null;
}

export interface IAccessRulesBlock {
  properties: IPropertyRule[];
}

/** Shape of an entity fragment that may carry access-rule metadata. */
export interface IWithAccessRules {
  '@accessRules'?: IAccessRulesBlock;
  virtual?: {
    '@accessRules'?: IAccessRulesBlock;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}
